import '../css/main.css';
import Alpine from 'alpinejs';

// Scroll reveal
const observer = new IntersectionObserver(
    entries => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                entry.target.classList.add('visible');
                observer.unobserve(entry.target);
            }
        });
    },
    { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
);

document.querySelectorAll('.reveal').forEach(el => observer.observe(el));

// Contact form Alpine component
Alpine.data('contactForm', () => ({
    form: {
        full_name: '',
        company: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
    },
    fieldErrors: {},
    loading: false,
    success: false,

    validateField(field) {
        const v = this.form[field]?.trim() ?? '';
        const errors = {};

        if (field === 'full_name') {
            if (!v) errors.full_name = 'Full name is required.';
            else if (v.length < 2) errors.full_name = 'Name must be at least 2 characters.';
        }
        if (field === 'email') {
            if (!v) errors.email = 'Email address is required.';
            else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) errors.email = 'Please enter a valid email address.';
        }
        if (field === 'subject') {
            if (!v) errors.subject = 'Subject is required.';
        }
        if (field === 'message') {
            if (!v) errors.message = 'Message is required.';
            else if (v.length < 10) errors.message = 'Message must be at least 10 characters.';
        }

        if (errors[field]) {
            this.fieldErrors[field] = errors[field];
        } else {
            delete this.fieldErrors[field];
        }
    },

    async submitForm() {
        ['full_name', 'email', 'subject', 'message'].forEach(f => this.validateField(f));
        if (Object.keys(this.fieldErrors).length) return;

        this.loading = true;
        const config = window.delansysContact ?? {};

        try {
            const res = await fetch(config.restUrl ?? '/wp-json/delansys/v1/contact', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': config.nonce ?? '',
                },
                body: JSON.stringify({ ...this.form, lang: config.lang ?? 'en' }),
            });

            const data = await res.json();

            if (data.success) {
                this.success = true;
                this.form = { full_name: '', company: '', email: '', phone: '', subject: '', message: '' };
                this.fieldErrors = {};
            } else if (data.errors) {
                this.fieldErrors = data.errors;
            }
        } catch (e) {
            console.error('Contact form error:', e);
        } finally {
            this.loading = false;
        }
    },
}));

// Sticky header
Alpine.data('stickyHeader', () => ({
    scrolled: false,
    init() {
        let ticking = false;
        window.addEventListener('scroll', () => {
            if (!ticking) {
                requestAnimationFrame(() => {
                    this.scrolled = window.scrollY > 10;
                    ticking = false;
                });
                ticking = true;
            }
        }, { passive: true });
    },
}));

// Mobile menu
Alpine.data('mobileMenu', () => ({
    open: false,
    toggle() { this.open = !this.open; },
    close() { this.open = false; },
}));

// Subscribe modal
Alpine.data('subscribeModal', () => ({
    open: false,
    email: '',
    website: '',
    loading: false,
    success: false,
    errors: {},
    categories: [],
    checkedParents: [],
    optedOutTerms: [],

    openModal(detail) {
        this.open = true;
        this.success = false;
        this.errors = {};
        this.email = '';
        this.website = '';
        this.optedOutTerms = [];
        const config = window.delansysSubscribe ?? {};
        this.categories = config.categories ?? [];
        const section = detail?.section || 'news';
        const slugs = this.categories.map(c => this.parentKey(c));
        this.checkedParents = slugs.includes(section) ? [section] : [...slugs];
        document.body.style.overflow = 'hidden';
    },

    closeModal() {
        this.open = false;
        document.body.style.overflow = '';
    },

    parentKey(cat) {
        return cat.slug === 'press' ? 'press' : 'news';
    },

    isParentChecked(cat) {
        return this.checkedParents.includes(this.parentKey(cat));
    },

    isChildChecked(cat, child) {
        if (!this.isParentChecked(cat)) return false;
        return !this.optedOutTerms.includes(child.id);
    },

    toggleParent(cat, checked) {
        const key = this.parentKey(cat);
        if (checked) {
            if (!this.checkedParents.includes(key)) {
                this.checkedParents = [...this.checkedParents, key];
            }
            const childIds = (cat.children || []).map(c => c.id);
            this.optedOutTerms = this.optedOutTerms.filter(id => !childIds.includes(id));
        } else {
            this.checkedParents = this.checkedParents.filter(p => p !== key);
        }
    },

    toggleChild(cat, child, checked) {
        if (checked) {
            this.optedOutTerms = this.optedOutTerms.filter(id => id !== child.id);
            if (!this.isParentChecked(cat)) {
                this.checkedParents = [...this.checkedParents, this.parentKey(cat)];
            }
        } else {
            if (!this.optedOutTerms.includes(child.id)) {
                this.optedOutTerms = [...this.optedOutTerms, child.id];
            }
            const allChildIds = (cat.children || []).map(c => c.id);
            if (allChildIds.length > 0 && allChildIds.every(id => this.optedOutTerms.includes(id))) {
                this.checkedParents = this.checkedParents.filter(p => p !== this.parentKey(cat));
            }
        }
    },

    validateEmail() {
        const v = this.email.trim();
        if (!v) {
            this.errors = { ...this.errors, email: 'Email address is required.' };
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
            this.errors = { ...this.errors, email: 'Please enter a valid email address.' };
        } else {
            const { email, ...rest } = this.errors;
            this.errors = rest;
        }
    },

    async submitForm() {
        this.errors = {};

        const email = this.email.trim();
        if (!email) {
            this.errors.email = 'Email address is required.';
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            this.errors.email = 'Please enter a valid email address.';
            return;
        }
        if (this.checkedParents.length === 0) {
            this.errors.type = 'Please select at least one subscription type.';
            return;
        }

        this.loading = true;
        const config = window.delansysSubscribe ?? {};

        try {
            const res = await fetch(config.restUrl ?? '/wp-json/delansys/v1/subscribe', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': config.nonce ?? '',
                },
                body: JSON.stringify({
                    email: email,
                    parents: [...this.checkedParents],
                    opted_out_terms: [...this.optedOutTerms],
                    website: this.website,
                    lang: config.lang ?? 'en',
                }),
            });

            const data = await res.json();

            if (data.success) {
                this.success = true;
            } else if (data.errors) {
                this.errors = data.errors;
            } else if (data.message) {
                this.errors.server = data.message;
            }
        } catch (e) {
            this.errors.server = 'An error occurred. Please try again.';
        } finally {
            this.loading = false;
        }
    },
}));

Alpine.data('subscriptionManager', () => ({
    loading: true,
    ready: false,
    saving: false,
    saved: false,
    unsubscribed: false,
    fetchError: '',
    saveError: '',
    heading: '',
    subheading: '',
    txt: {},
    categories: [],
    checkedParents: [],
    optedOutTerms: [],
    token: '',
    newsletterId: 0,
    restUrl: '',

    init() {
        this.token        = this.$el.dataset.token        || '';
        this.newsletterId = parseInt(this.$el.dataset.newsletterId || '0', 10) || 0;
        this.restUrl      = this.$el.dataset.restUrl      || '/wp-json/delansys/v1/manage-subscription';
        this.heading    = this.$el.dataset.heading    || 'Manage Your Subscriptions';
        this.subheading = this.$el.dataset.subheading || 'Choose what you\'d like to receive.';
        try {
            this.txt = JSON.parse(this.$el.dataset.txt || '{}');
        } catch (e) {
            this.txt = {};
        }
        this.fetchData();
    },

    async fetchData() {
        this.loading = true;
        try {
            const res  = await fetch(`${this.restUrl}?token=${encodeURIComponent(this.token)}`);
            const data = await res.json();
            if (!data.success) {
                this.fetchError = data.message || 'Invalid link.';
            } else {
                this.categories    = data.categories || [];
                this.optedOutTerms = data.opted_out_terms || [];
                this.checkedParents = this.typeToParents(data.type || 'news');
                this.ready = true;
            }
        } catch (e) {
            this.fetchError = 'An error occurred. Please try again.';
        } finally {
            this.loading = false;
        }
    },

    typeToParents(type) {
        if (type === 'both')  return ['news', 'press'];
        if (type === 'press') return ['press'];
        return ['news'];
    },

    parentKey(cat) {
        return cat.slug === 'press' ? 'press' : 'news';
    },

    isParentChecked(cat) {
        return this.checkedParents.includes(this.parentKey(cat));
    },

    isChildChecked(cat, child) {
        if (!this.isParentChecked(cat)) return false;
        return !this.optedOutTerms.includes(child.id);
    },

    toggleParent(cat, checked) {
        const key = this.parentKey(cat);
        if (checked) {
            if (!this.checkedParents.includes(key)) {
                this.checkedParents = [...this.checkedParents, key];
            }
            // Remove this parent's children from opted-out
            const childIds = (cat.children || []).map(c => c.id);
            this.optedOutTerms = this.optedOutTerms.filter(id => !childIds.includes(id));
        } else {
            this.checkedParents = this.checkedParents.filter(p => p !== key);
        }
    },

    toggleChild(cat, child, checked) {
        if (checked) {
            this.optedOutTerms = this.optedOutTerms.filter(id => id !== child.id);
            // Ensure parent is enabled
            if (!this.isParentChecked(cat)) {
                const key = this.parentKey(cat);
                this.checkedParents = [...this.checkedParents, key];
            }
        } else {
            if (!this.optedOutTerms.includes(child.id)) {
                this.optedOutTerms = [...this.optedOutTerms, child.id];
            }
            // If all siblings are now opted out, uncheck parent too
            const allChildIds = (cat.children || []).map(c => c.id);
            if (allChildIds.length > 0 && allChildIds.every(id => this.optedOutTerms.includes(id))) {
                const key = this.parentKey(cat);
                this.checkedParents = this.checkedParents.filter(p => p !== key);
            }
        }
    },

    async savePreferences() {
        this.saving    = true;
        this.saveError = '';
        try {
            const res  = await fetch(this.restUrl, {
                method:  'POST',
                headers: { 'Content-Type': 'application/json' },
                body:    JSON.stringify({
                    token:           this.token,
                    newsletter_id:   this.newsletterId,
                    parents:         [...this.checkedParents],
                    opted_out_terms: [...this.optedOutTerms],
                }),
            });
            const data = await res.json();
            if (data.success) {
                if (data.unsubscribed) {
                    this.unsubscribed = true;
                } else {
                    this.saved = true;
                }
            } else {
                this.saveError = data.message || (this.txt.server_error || 'An error occurred. Please try again.');
            }
        } catch (e) {
            this.saveError = this.txt.server_error || 'An error occurred. Please try again.';
        } finally {
            this.saving = false;
        }
    },

    async unsubscribeAll() {
        this.saving    = true;
        this.saveError = '';
        try {
            const res  = await fetch(this.restUrl, {
                method:  'POST',
                headers: { 'Content-Type': 'application/json' },
                body:    JSON.stringify({
                    token:           this.token,
                    newsletter_id:   this.newsletterId,
                    parents:         [],
                    opted_out_terms: [],
                }),
            });
            const data = await res.json();
            if (data.success) {
                this.unsubscribed = true;
            } else {
                this.saveError = data.message || (this.txt.server_error || 'An error occurred. Please try again.');
            }
        } catch (e) {
            this.saveError = this.txt.server_error || 'An error occurred. Please try again.';
        } finally {
            this.saving = false;
        }
    },
}));

window.Alpine = Alpine;
Alpine.start();
